// Sidebar.tsx
import React, { useEffect, useMemo } from 'react'
import {
    Badge,
    Box,
    Divider,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Theme,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material'
import {
    ChevronLeft as ChevronLeftIcon,
    Menu as MenuIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Sync as SyncIcon,
} from '@mui/icons-material'

import { sidebarHighlight } from '../../theme'
import { useAuthStore } from '../../store/authStore'
import GroupsIcon from '@mui/icons-material/Groups'
import LogoutIcon from '@mui/icons-material/Logout'
import { useAppStore } from '../../store/appStore'
import { get, post } from '../../api/api'
import { showMessage } from '../../utils/snackbar'
import InfoOutlineIcon from '@mui/icons-material/Info'
import PaymentsIcon from '@mui/icons-material/Payments'
import { truncate } from '../../utils/string'
import { useLocation, useNavigate } from 'react-router-dom'
import MicIcon from '@mui/icons-material/Mic'
import HistoryIcon from '@mui/icons-material/History'
import DescriptionIcon from '@mui/icons-material/Description'
import BugReportIcon from '@mui/icons-material/BugReport'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import { openIntercomMessenger } from '../../hooks/useIntercom'
import { ROUTES } from '../../routes'
import { useIsUserFromMedendoTeam } from '../../hooks/useIsUserFromMedendoTeam'
import { useHasPermission } from '../../hooks/useHasPermission'
import { isReadOnly } from '../../models/UserModel'
import { useTranslation } from 'react-i18next'

interface MenuItem {
    name: string
    icon: React.ReactNode
    click_navigate: () => string
    route: string
}

type MenuMap = Record<string, MenuItem>

export const MENU: MenuMap = {
    NewEncounter: {
        name: 'Opnemen',
        icon: <MicIcon />,
        click_navigate: () => `${ROUTES.encounters.new.path}?ts=${Date.now()}`,
        route: ROUTES.encounters.new.path,
    },
    History: {
        name: 'Gesprekken',
        icon: <HistoryIcon />,
        click_navigate: () => ROUTES.encounters.list.path,
        route: ROUTES.encounters.list.path,
    },
    Templates: {
        name: 'Templates',
        icon: <DescriptionIcon />,
        click_navigate: () => ROUTES.templates.list.path,
        route: ROUTES.templates.list.path,
    },
    Admin: {
        name: 'Beheer',
        icon: <SupervisorAccountIcon />,
        click_navigate: () => ROUTES.admin.users.path,
        route: ROUTES.admin.users.path,
    },
    Integrations: {
        name: 'Integraties',
        icon: <SyncIcon />,
        click_navigate: () => ROUTES.integrations.list.path,
        route: ROUTES.integrations.list.path,
    },
} as const

interface SidebarProps {
    drawerWidth: number
    collapsedWidth: number
    handleLogout: () => void
    open: boolean
    onToggle: () => void
    isMobile: boolean
}

export const headerHeight = 63

const listItemButtonStyles = (isOpen: boolean) => ({
    minHeight: 48,
    flexDirection: isOpen ? 'row' : 'column',
    alignItems: 'center',
    gap: isOpen ? 0 : 0.5,
    '&.Mui-selected': {
        backgroundColor: sidebarHighlight,
        '&:hover': {
            backgroundColor: sidebarHighlight,
        },
    },
})

const listItemIconStyles = (isOpen: boolean, isActive: boolean) => ({
    minWidth: 0,
    mr: isOpen ? 3 : 'auto',
    justifyContent: 'center',
    ...(isActive && { color: 'primary.main' }),
})

const listItemTextStyles = (isOpen: boolean, animateLabel: boolean, theme: Theme) => ({
    textAlign: isOpen ? 'left' : 'center',
    '& .MuiTypography-root': isOpen
        ? {}
        : {
              fontWeight: 300,
              fontSize: '8px',
              lineHeight: 1.2,
              ...(animateLabel && {
                  // Hold the tiny label invisible until the drawer's width transition
                  // finishes, otherwise it pops in while the drawer is still wide.
                  animation: `sidebar-label-fade-in 150ms ease ${theme.transitions.duration.enteringScreen}ms backwards`,
                  '@keyframes sidebar-label-fade-in': {
                      from: { opacity: 0 },
                      to: { opacity: 1 },
                  },
                  '@media (prefers-reduced-motion: reduce)': {
                      animation: 'none',
                  },
              }),
          },
})

const drawerStyles = (open: boolean, drawerWidth: number, collapsedWidth: number, theme: Theme) => ({
    width: open ? drawerWidth : collapsedWidth,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        width: open ? drawerWidth : collapsedWidth,
        boxSizing: 'border-box',
        borderRadius: '0',
        padding: '0',
        backgroundColor: 'background.default',
        overflowX: 'hidden',
        // MUI sizes the paper with height:100%, which phones resolve against the large
        // viewport (browser chrome collapsed) — the drawer bottom ends up hidden behind
        // the URL bar. dvh tracks the visible height; browsers without it keep the 100%.
        height: '100dvh',
        transition: theme.transitions.create(['width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
        display: 'flex',
        flexDirection: 'column',
    },
})

interface MenuItemProps {
    item: MenuItem
    isOpen: boolean
    animateLabel: boolean
    isActive: boolean
    onClick: () => void
    disabled?: boolean
}

const SidebarMenuItem: React.FC<MenuItemProps> = ({ item, isOpen, animateLabel, isActive, onClick, disabled }) => {
    const theme = useTheme()
    return (
        <ListItem key={item.name} disablePadding sx={{ display: 'block' }}>
            <ListItemButton selected={isActive} onClick={onClick} disabled={disabled} sx={listItemButtonStyles(isOpen)}>
                <ListItemIcon sx={listItemIconStyles(isOpen, isActive)}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.name} sx={listItemTextStyles(isOpen, animateLabel, theme)} />
            </ListItemButton>
        </ListItem>
    )
}

export const Sidebar: React.FC<SidebarProps> = (props: SidebarProps) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const isUserFromMedendoTeam = useIsUserFromMedendoTeam()
    const payload = useAuthStore((state) => state.payload)
    const orgModel = useAppStore((state) => state.orgModel)
    const groups = useAppStore((state) => state.groups)
    const canCreateReports = useHasPermission('can_create_reports')
    const canManageUsersInOrg = useHasPermission('org_admin') && orgModel !== null
    // Org admins manage every group; group admins see the tab for the groups they administer.
    // Plain members reach their groups through Settings → Your groups.
    const canSeeGroups = canManageUsersInOrg || groups.some((g) => g.role === 'admin' || g.role === 'template_admin')
    const userModel = useAppStore((state) => state.userModel)
    const isOffboarded = isReadOnly(userModel)
    const isSoloUser = userModel !== null && userModel.org_id === null
    const isIntercomReady = useAppStore((state) => state.isIntercomReady)
    const intercomUnreadCount = useAppStore((state) => state.intercomUnreadCount)
    const frontendVersion = import.meta.env.VITE_VERSION ?? 'dev'
    const [backendVersion, setBackendVersion] = React.useState<string | null>(null)
    const location = useLocation()

    const navigate = useNavigate()

    const translatedMenu = useMemo(
        () => ({
            NewEncounter: {
                name: t('encounter.menu_record'),
                icon: <MicIcon />,
                click_navigate: () => `${ROUTES.encounters.new.path}?ts=${Date.now()}`,
                route: ROUTES.encounters.new.path,
            },
            History: {
                name: t('encounter.menu_history'),
                icon: <HistoryIcon />,
                click_navigate: () => ROUTES.encounters.list.path,
                route: ROUTES.encounters.list.path,
            },
            Templates: {
                name: t('encounter.menu_templates'),
                icon: <DescriptionIcon />,
                click_navigate: () => ROUTES.templates.list.path,
                route: ROUTES.templates.list.path,
            },
            Admin: {
                name: t('encounter.menu_admin'),
                icon: <SupervisorAccountIcon />,
                click_navigate: () => ROUTES.admin.users.path,
                route: ROUTES.admin.users.path,
            },
            Groups: {
                name: t('encounter.menu_groups'),
                icon: <GroupsIcon />,
                click_navigate: () => ROUTES.groups.list.path,
                route: ROUTES.groups.list.path,
            },
            Integrations: {
                name: t('encounter.menu_integrations'),
                icon: <SyncIcon />,
                click_navigate: () => ROUTES.integrations.list.path,
                route: ROUTES.integrations.list.path,
            },
        }),
        [t]
    )

    // Most-specific match wins: /e/new should not activate /e
    const activeMenuItem = useMemo(() => {
        const matches = Object.values(translatedMenu).filter(
            (item) => location.pathname === item.route || location.pathname.startsWith(item.route + '/')
        )
        if (!matches.length) return null
        return matches.reduce((best, curr) => (curr.route.length > best.route.length ? curr : best), matches[0])
    }, [location.pathname, translatedMenu])

    useEffect(() => {
        const fetchVersion = async () => {
            try {
                const version = await get<string>('/version')
                setBackendVersion(version)
                console.info('[Sidebar] Backend version: ', version)
            } catch (error) {
                console.error('Failed to fetch version:', error)
            }
        }
        fetchVersion()
        console.info('[Sidebar] Frontend version: ', import.meta.env.VITE_VERSION)
    }, [])

    // Drives the portal menu item's disabled state and guards against a double-click
    // minting a second Stripe billing_portal.Session request while the first is in flight.
    const [isOpeningPortal, setIsOpeningPortal] = React.useState(false)

    const handleOpenBillingPortal = async () => {
        if (isOpeningPortal) return
        setIsOpeningPortal(true)
        // Opened synchronously, still inside the click, and navigated once the URL arrives:
        // opening the tab only after the awaited POST below resolves would no longer count
        // as user-initiated, so browsers (Safari in particular) would block it as a popup.
        // noopener/noreferrer would make window.open() return null, leaving nothing to
        // navigate later, so the opener link is severed by hand instead, right below.
        const portalWindow = window.open('', '_blank')
        if (portalWindow) portalWindow.opener = null

        if (!portalWindow) {
            // Already blocked before we even had a URL — no point minting a Stripe session
            // nobody can reach.
            showMessage(t('encounter.customer_portal_error'), 'error')
            setIsOpeningPortal(false)
            return
        }

        try {
            // retries: 0 — matches AccountStatus.tsx's own renewal call: a silently
            // auto-retried POST here shouldn't be the frontend's default behavior for a
            // user-triggered navigation.
            const { url } = await post<{ url: string }>('/me/billing-portal-session', {}, 0, 1000)
            portalWindow.location.href = url
        } catch (error) {
            console.warn('Failed to start a billing portal session:', error)
            showMessage(t('encounter.customer_portal_error'), 'error')
            portalWindow.close()
        } finally {
            setIsOpeningPortal(false)
        }
    }

    // On mobile the drawer is an overlay: whenever it's shown, it's shown expanded —
    // there's no collapsed icon-rail state like on desktop.
    const isOpen = props.isMobile || props.open

    // Animate the collapsed labels only after a real collapse: on first mount there is
    // no width transition to wait for, so the labels must be visible immediately.
    const [hasExpanded, setHasExpanded] = React.useState(false)
    if (props.open && !hasExpanded) setHasExpanded(true)
    const animateLabel = hasExpanded && !isOpen

    // On mobile the drawer overlays the page it navigated to, so close it after selecting.
    const handleNavigate = (path: string) => {
        navigate(path)
        if (props.isMobile) props.onToggle()
    }

    // On mobile the drawer would cover the messenger it just opened.
    const handleSupport = () => {
        openIntercomMessenger()
        if (props.isMobile) props.onToggle()
    }

    return (
        <Drawer
            sx={drawerStyles(isOpen, props.drawerWidth, props.collapsedWidth, theme)}
            variant={props.isMobile ? 'temporary' : 'permanent'}
            anchor="left"
            open={props.open}
            onClose={props.onToggle}
            ModalProps={{
                keepMounted: true,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'column',
                    height: '100%',
                }}
            >
                {/* Top section - grows to push content down */}
                <Box sx={{ flex: 1 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: isOpen ? 'right' : 'center',
                            height: headerHeight,
                        }}
                    >
                        <IconButton
                            onClick={props.onToggle}
                            aria-label={t('encounter.menu_open')}
                            aria-expanded={isOpen}
                        >
                            {isOpen ? <ChevronLeftIcon /> : <MenuIcon />}
                        </IconButton>
                    </Box>
                    <Divider />

                    <List>
                        {Object.values(translatedMenu).map((item) => {
                            // An offboarded user reaches nothing but their own history, read-only (MD-1497).
                            if (isOffboarded && item.name !== translatedMenu.History.name) return null
                            if (item.name === translatedMenu.NewEncounter.name && !canCreateReports) return null
                            if (item.name === translatedMenu.Admin.name && !canManageUsersInOrg) return null
                            if (item.name === translatedMenu.Groups.name && !canSeeGroups) return null
                            if (item.name === translatedMenu.Integrations.name && !orgModel?.ehr_adapter) return null
                            return (
                                <SidebarMenuItem
                                    key={item.name}
                                    item={item}
                                    isOpen={isOpen}
                                    animateLabel={animateLabel}
                                    isActive={item === activeMenuItem}
                                    onClick={() => handleNavigate(item.click_navigate())}
                                />
                            )
                        })}

                        {/* Deliberately not hidden from an offboarded user, unlike everything else
                            above: the portal is where a lapsed solo subscriber reviews their
                            invoices and payment method, so it is more relevant to them than to
                            an active one, not less (MD-1497). */}
                        {isSoloUser && (
                            <>
                                <Divider sx={{ mt: '8px' }} />
                                <SidebarMenuItem
                                    item={{
                                        name: t('encounter.customer_portal'),
                                        icon: <PaymentsIcon />,
                                        click_navigate: () => '',
                                        route: '',
                                    }}
                                    isOpen={isOpen}
                                    animateLabel={animateLabel}
                                    isActive={false}
                                    onClick={handleOpenBillingPortal}
                                    disabled={isOpeningPortal}
                                />
                            </>
                        )}
                        {isUserFromMedendoTeam && !isOffboarded && (
                            <>
                                <Divider sx={{ mt: '32px' }} />
                                <Typography align="right" fontSize="8px" fontWeight="300" width="100%">
                                    {frontendVersion} / {backendVersion}
                                </Typography>

                                <Divider sx={{ mb: '8px' }} />
                                <SidebarMenuItem
                                    item={{
                                        name: 'Dev',
                                        icon: <BugReportIcon />,
                                        click_navigate: () => ROUTES.dev.panel.path,
                                        route: ROUTES.dev.panel.path,
                                    }}
                                    isOpen={isOpen}
                                    animateLabel={animateLabel}
                                    isActive={location.pathname.startsWith(ROUTES.dev.panel.path)}
                                    onClick={() => handleNavigate(ROUTES.dev.panel.path)}
                                />
                            </>
                        )}
                    </List>
                </Box>

                {/* Bottom section - fixed at bottom */}
                <Box sx={{ mt: 'auto' }}>
                    <List>
                        {isIntercomReady && (
                            <SidebarMenuItem
                                item={{
                                    name: t('encounter.menu_support'),
                                    icon: (
                                        <Badge badgeContent={intercomUnreadCount} color="error">
                                            <SupportAgentIcon />
                                        </Badge>
                                    ),
                                    click_navigate: () => '',
                                    route: '',
                                }}
                                isOpen={isOpen}
                                animateLabel={animateLabel}
                                isActive={false}
                                onClick={handleSupport}
                            />
                        )}
                        <SidebarMenuItem
                            item={{
                                name: t('encounter.logout'),
                                icon: <LogoutIcon />,
                                click_navigate: () => '',
                                route: '',
                            }}
                            isOpen={isOpen}
                            animateLabel={animateLabel}
                            isActive={false}
                            onClick={props.handleLogout}
                        />
                    </List>

                    <Divider />
                    <Stack
                        direction={props.open ? 'row' : 'column'}
                        alignItems="center"
                        justifyContent="center"
                        spacing={1}
                        sx={{ overflow: 'hidden', whiteSpace: 'nowrap', p: 1 }}
                    >
                        <Tooltip title={t('encounter.menu_settings')} placement="top">
                            <IconButton
                                size="small"
                                onClick={() => handleNavigate(ROUTES.settings.main.path)}
                                aria-label={t('encounter.menu_settings')}
                            >
                                <AccountCircleIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                        {props.open && (
                            <Typography variant="body2" fontSize="12px" fontWeight="300">
                                {truncate(payload?.email ?? '', 23)}
                            </Typography>
                        )}

                        {props.open && (
                            <Tooltip
                                title={
                                    <Typography variant="caption" align="left" display="block">
                                        {frontendVersion} / {backendVersion}
                                    </Typography>
                                }
                                placement="top"
                                slotProps={{
                                    popper: {
                                        sx: {
                                            '& .MuiTooltip-tooltip': {
                                                backgroundColor: '#ffffff',
                                                color: '#000',
                                                boxShadow: 1,
                                            },
                                        },
                                    },
                                }}
                            >
                                <IconButton size="small">
                                    <InfoOutlineIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Stack>
                </Box>
            </Box>
        </Drawer>
    )
}
